import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { api, errMessage } from "../lib/api";

const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// Parent-controlled practice reminders: off by default, time + days, pause.
export const ReminderSettings = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  // Star-day emails: a morning nudge when a streak star lands today.
  const [starOn, setStarOn] = useState(null);

  useEffect(() => {
    api.get("/reminders/settings").then(({ data }) => setSettings(data.settings)).catch(() => {});
    api.get("/recap/star-reminder").then(({ data }) => setStarOn(data.enabled)).catch(() => {});
  }, []);

  const saveStar = async (enabled) => {
    setSaving(true);
    try {
      const { data } = await api.put("/recap/star-reminder", { enabled });
      setStarOn(data.enabled);
      toast.success(enabled ? "Star-day emails on" : "Star-day emails off");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const save = async (next) => {
    setSaving(true);
    try {
      const payload = { ...next, tz_offset_minutes: new Date().getTimezoneOffset() };
      const { data } = await api.put("/reminders/settings", payload);
      setSettings(data.settings);
      toast.success(next.enabled ? "Practice reminders updated" : "Practice reminders turned off");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;
  const paused = settings.paused_until && settings.paused_until > new Date().toISOString();

  return (
    <div className="bg-card rounded-2xl border border-border p-5 space-y-4" data-testid="reminder-settings">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BellRing className="h-4 w-4 text-secondary" aria-hidden="true" />
          <p className="text-sm font-bold">Practice reminders</p>
        </div>
        <Switch checked={settings.enabled} disabled={saving}
          onCheckedChange={(v) => save({ ...settings, enabled: v })}
          data-testid="reminder-toggle" />
      </div>
      <p className="text-xs text-muted-foreground">
        A brief, encouraging email at your chosen time — it never includes your child's name, progress or stories.
      </p>
      {settings.enabled && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <label htmlFor="reminder-time" className="text-xs font-semibold">Daily at</label>
            <input id="reminder-time" type="time" disabled={saving}
              value={`${String(settings.hour).padStart(2, "0")}:${String(settings.minute).padStart(2, "0")}`}
              onChange={(e) => {
                const [h, m] = e.target.value.split(":").map(Number);
                save({ ...settings, hour: h, minute: m });
              }}
              className="h-9 rounded-lg border border-border bg-background px-3 text-sm"
              data-testid="reminder-time-input" />
            <span className="text-xs text-muted-foreground">(your local time)</span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-semibold mr-1">Days:</span>
            {DAY_LABELS.map((label, day) => {
              const active = settings.days.length === 0 || settings.days.includes(day);
              return (
                <button key={label} type="button" disabled={saving}
                  onClick={() => {
                    const current = settings.days.length === 0 ? [0, 1, 2, 3, 4, 5, 6] : settings.days;
                    const next = current.includes(day) ? current.filter((d) => d !== day) : [...current, day];
                    if (next.length === 0) {
                      toast.info("Pick at least one day — or use the toggle above to turn reminders off");
                      return;
                    }
                    save({ ...settings, days: next.length === 7 ? [] : next });
                  }}
                  className={`h-8 w-8 rounded-full text-xs font-bold border transition-colors ${active ? "bg-secondary text-secondary-foreground border-secondary" : "bg-card border-border"}`}
                  data-testid={`reminder-day-${day}`}>
                  {label}
                </button>
              );
            })}
          </div>
          <Button variant="outline" size="sm" disabled={saving} className="rounded-full text-xs"
            onClick={() => save({
              ...settings,
              paused_until: paused ? null : new Date(Date.now() + 7 * 86400000).toISOString(),
            })}
            data-testid="reminder-pause-button">
            {paused ? "Resume reminders" : "Pause for a week"}
          </Button>
        </div>
      )}
      {starOn !== null && (
        <div className="flex items-center justify-between gap-3 border-t border-border pt-4">
          <div className="min-w-0">
            <p className="text-sm font-bold">Star-day emails</p>
            <p className="text-xs text-muted-foreground">
              A morning heads-up when a streak star (5, 7, 10, 14 or 30 days in a row) lands today.
            </p>
          </div>
          <Switch checked={starOn} disabled={saving}
            onCheckedChange={saveStar}
            data-testid="star-reminder-toggle" />
        </div>
      )}
    </div>
  );
};
